import { ReactNode, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { WLIDS } from 'Constants';
import { store } from 'Models/Store';
import {
	StandbyValues,
	useStandbyForm,
} from 'Views/Components/_HumanWritten/CheckIn/FerryCheckIn/CheckInStandby/useStandbyForm';
import { Flex } from 'Views/Components/Flex/Flex';
import { NewModal } from 'Views/Components/Modal/NewModal';
import { MessageBox } from 'Views/Components/_HumanWritten/MessageBox/MessageBox';
import { getMyStandbyCount } from 'Services/Api/_HumanWritten/StandbyServiceApi';
import { useQuery } from '@tanstack/react-query';
import { TextInput } from 'Views/Components/New/TextInput';
import { PhoneInput } from 'Views/Components/New/PhoneInput';

interface JoinStandbyModalProps {
	ferryTripIds: string[];
	onClose: () => void;
	message?: ReactNode;
	defaultValues?: StandbyValues;
}

export function JoinStandbyModal({ ferryTripIds, onClose, message, defaultValues }: JoinStandbyModalProps) {
	const { name, setName, email, setEmail, phone, setPhone, setUserId, errors, setErrors, addRequest, onSubmit } =
		useStandbyForm({
			ferryTripIds,
			onCompletion: onClose,
			defaultValues,
		});

	useEffect(() => {
		// Prefill details
		if (!defaultValues && store.userId && store.userData) {
			setName(`${store.userData.firstName} ${store.userData.lastName}`);
			setEmail(store.userData.email);
			setPhone(store.userData.phone);
			setUserId(store.userId);
		}
	}, [store.userData]);

	const { data: existingCount } = useQuery({
		queryKey: ['standby', 'mine', 'count', ...ferryTripIds],
		queryFn: () => getMyStandbyCount(ferryTripIds),
		enabled: !!store.userId,
		// Always read afresh: the app-wide 30-second cache showed the old count straight after joining.
		staleTime: 0,
		refetchOnMount: 'always',
	});

	// A client-written message for the top of the modal, blank by default. Read raw rather than through
	// FormattedMessage, which falls back to printing the key when both the translation and the default
	// are empty.
	const intl = useIntl();
	const rawIntroMessage = intl.messages[WLIDS.standby.modalMessage];
	const hasIntroMessage =
		typeof rawIntroMessage === 'string' ? rawIntroMessage.trim() !== '' : !!rawIntroMessage?.length;

	const onCancel = () => {
		onClose();
	};

	const onConfirm = () => {
		onSubmit(null);
	};

	return (
		<>
			<NewModal.Header>
				<NewModal.Title>
					<FormattedMessage id={WLIDS.standby.modalTitle} defaultMessage="Join standby" />
				</NewModal.Title>
			</NewModal.Header>
			<form onSubmit={onSubmit}>
				<NewModal.Body>
					<Flex className="standby" direction="col" gap="xs">
						{hasIntroMessage && (
							<p className="standby__intro-message" data-test-id="join-standby-intro-message">
								{intl.formatMessage({ id: WLIDS.standby.modalMessage })}
							</p>
						)}
						{message && <>{message}</>}
						{/* The signed-in customer's own entries on this sailing; a guest has none to count. */}
						{(existingCount ?? 0) > 0 && (
							<MessageBox status="info" slim noBottomMargin testId="join-standby-already-on">
								<FormattedMessage
									id={WLIDS.standby.alreadyOnThisTripCount}
									defaultMessage="{count, plural, one {You are already on the standby list for this sailing.} other {You are already on the standby list for this sailing # times.}}"
									values={{ count: existingCount }}
								/>
							</MessageBox>
						)}
						<TextInput
							label="Name"
							required
							disabled={addRequest.isPending}
							value={name ?? ''}
							error={errors.name}
							onChange={e => {
								setName(e.target.value);
								if (errors.name) {
									setErrors(prevErrors => {
										const newErrors = { ...prevErrors };
										delete newErrors.name;
										return newErrors;
									});
								}
							}}
						/>
						<TextInput
							label="Email"
							required
							disabled={addRequest.isPending}
							value={email ?? ''}
							error={errors.email}
							onChange={e => {
								setEmail(e.target.value);
								if (errors.email) {
									setErrors(prevErrors => {
										const newErrors = { ...prevErrors };
										delete newErrors.email;
										return newErrors;
									});
								}
							}}
						/>
						<PhoneInput
							value={phone}
							label="Phone"
							required
							disabled={addRequest.isPending}
							onChange={value => {
								setPhone(value);
								if (errors.phone) {
									setErrors(prevErrors => {
										const newErrors = { ...prevErrors };
										delete newErrors.phone;
										return newErrors;
									});
								}
							}}
							error={errors.phone}
						/>
					</Flex>
				</NewModal.Body>
				<NewModal.Footer>
					<NewModal.CancelButton onClick={onCancel}>Cancel</NewModal.CancelButton>
					<NewModal.ConfirmButton onClick={onConfirm}>Join list</NewModal.ConfirmButton>
				</NewModal.Footer>
			</form>
		</>
	);
}
